import { useCreditPricing } from "hooks/useCreditPricing";
import type { CreditsSnapshot } from "hooks/useCredits";

interface CreditsCardProps {
  credits: CreditsSnapshot | null;
  isLoading?: boolean;
  onTopupCredit: () => void;
}

const formatDate = (date: string) =>
  new Date(date).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

/** "Resets in 4 days (Mon, 10 Aug 2026)", degrading gracefully when the server hasn't sent a date. */
const buildTimingLabel = (
  verb: string,
  daysRemaining: number | null,
  date: string | null,
  fallback: string
) => {
  if (daysRemaining === null && !date) return fallback;

  const parts: string[] = [];
  if (daysRemaining !== null) {
    parts.push(
      daysRemaining === 0
        ? `${verb} today`
        : `${verb} in ${daysRemaining} ${daysRemaining === 1 ? "day" : "days"}`
    );
  } else {
    parts.push(verb);
  }

  return date ? `${parts[0]} (${formatDate(date)})` : parts[0];
};

const barColor = (percentage: number) => {
  if (percentage >= 70) return "#18B368";
  if (percentage >= 30) return "#EDA12F";
  return "#E74444";
};

/**
 * Credit balance summary for the Credits & Billing page.
 *
 * Two independent buckets: the free weekly allowance that refreshes every week,
 * and purchased credits that stay valid for a fixed window from purchase.
 */
const CreditsCard: React.FC<CreditsCardProps> = ({ credits, isLoading = false, onTopupCredit }) => {
  const { pricing } = useCreditPricing();

  if (isLoading || !credits) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-sm flex items-center justify-center min-h-[360px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4" />
          <p className="text-gray-600">Loading credits...</p>
        </div>
      </div>
    );
  }

  const { weekly, purchased, legacy, legacyNote } = credits;
  const weeklyPercentage =
    weekly.allowance > 0 ? Math.min(100, (weekly.balance / weekly.allowance) * 100) : 0;

  const resetLabel = buildTimingLabel("Resets", weekly.daysRemaining, weekly.date, "Resets weekly");
  const expiryLabel = buildTimingLabel(
    "Expires",
    purchased.daysRemaining,
    purchased.date,
    `Valid for ${pricing.validityDays} days from purchase`
  );

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm flex flex-col">
      <h2 className="text-2xl font-bold text-[#012169] mb-8">Credits</h2>

      {/* Weekly allowance — granted to every account, grandfathered included. */}
      <section>
        <h3 className="text-base font-semibold text-[#1D2939] mb-3">Weekly Credits</h3>
        <p className="mb-3">
          <span
            className="text-3xl font-bold"
            style={{ color: barColor(weeklyPercentage) }}
          >
            {weekly.balance.toLocaleString()}
          </span>
          <span className="text-sm text-[#475467]">
            /{weekly.allowance.toLocaleString()} remaining
          </span>
        </p>
        <div
          className="w-full h-2 rounded-full bg-[#EAECF0] overflow-hidden"
          role="progressbar"
          aria-valuenow={weekly.balance}
          aria-valuemin={0}
          aria-valuemax={weekly.allowance}
          aria-label="Weekly credits remaining"
        >
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{ width: `${weeklyPercentage}%`, backgroundColor: barColor(weeklyPercentage) }}
          />
        </div>
        <p className="text-sm text-[#667085] mt-3">{resetLabel}</p>
      </section>

      <p className="mt-4 text-sm text-[#667085]">
        Spent first, purchased credits are only used once these run out.
      </p>

      <hr className="my-6 border-[#EAECF0]" />

      {/* Purchased balance */}
      <section>
        <h3 className="text-base font-semibold text-[#1D2939] mb-3">Purchased Credits</h3>
        <p className="mb-2">
          <span className="text-3xl font-bold text-[#18B368]">
            {purchased.balance.toLocaleString()}
          </span>{" "}
          <span className="text-sm text-[#475467]">Credits</span>
        </p>
        <p className="text-sm text-[#667085]">
          {purchased.balance > 0
            ? expiryLabel
            : `Top up anytime — purchased credits stay valid for ${pricing.validityDays} days`}
        </p>
      </section>

      {/* Credits carried over from a subscription still being honored. Only
          grandfathered accounts have these, and the bucket disappears once the
          legacy plan expires. */}
      {legacy > 0 && (
        <>
          <hr className="my-6 border-[#EAECF0]" />
          <section>
            <h3 className="text-base font-semibold text-[#1D2939] mb-3">Plan Credits</h3>
            <p className="mb-2">
              <span className="text-3xl font-bold text-[#012169]">{legacy.toLocaleString()}</span>{" "}
              <span className="text-sm text-[#475467]">Credits</span>
            </p>
            <p className="text-sm text-[#667085]">
              {legacyNote ?? "Carried over from your previous plan."}
            </p>
          </section>
        </>
      )}

      <button
        onClick={onTopupCredit}
        className="w-full mt-8 py-3 bg-[#012169] text-white rounded-lg font-medium hover:bg-[#011557] transition-colors"
      >
        Topup Credit
      </button>
    </div>
  );
};

export { CreditsCard };
